/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BLOCK_RESTRICTED_COUNTRIES
 * Date:         2026-01-18
 * Modified By:  Cursor
 *
 * Description:
 * Add validation to reject blocked countries (Belarus, Cuba, Iran, Iraq, 
 * North Korea, Russia) from company country field. Validation uses .notOneOf()
 * to prevent selection of restricted countries.
 *
 * File Size Check:
 * - Current: 29 lines
 * - Adding: ~2 lines (import + validation rule)
 * - Projected: ~31 lines
 * - Decision: Proceed (within 150-line limit)
 *
 * Related Files:
 * - client/src/components/setup/company-profile/utils/companyValidation.ts
 * - client/src/utils/countries.ts (BLOCKED_COUNTRIES export)
 */
import * as Yup from "yup";
// [2026-01-18] BLOCK_RESTRICTED_COUNTRIES: Import blocked countries for validation
import { BLOCKED_COUNTRIES } from "../../../../utils/countries";

const normalizeUrl = (value?: string | null): string => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// [2026-02-06] Updated URL validation messages with examples
export const companyValidationSchema = Yup.object({
  companyName: Yup.string().max(80),
  website: Yup.string()
    .transform(v => normalizeUrl(v) || "")
    .url("Please enter a valid URL (e.g. https://www.yourcompany.com)")
    .nullable()
    .notRequired(),
  companyEmail: Yup.string().email().nullable().notRequired(),
  // [2026-02-13] FIX_PHONE_VALIDATION: Aligned with user profile E.164 validation
  phone: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "phone-format-if-present",
      "Please enter a valid international phone number",
      (value) => {
        if (!value || !value.trim()) return true;
        const digits = value.replace(/\D/g, "");
        if (digits.length < 7 || digits.length > 15) return false;
        return /^[+]?[1-9]\d{6,14}$/.test(value.trim());
      },
    ),
  address1: Yup.string().max(120),
  address2: Yup.string().max(120),
  city: Yup.string().max(60),
  state: Yup.string().max(60),
  zip: Yup.string().max(20),
  country: Yup.string().max(60),
  companyLinkedIn: Yup.string()
    .transform(v => normalizeUrl(v) || "")
    .url("Please enter a valid URL (e.g. https://www.linkedin.com/company/yourcompany)")
    .nullable()
    .notRequired(),
  companyX: Yup.string()
    .transform(v => normalizeUrl(v) || "")
    .url("Please enter a valid URL (e.g. https://www.x.com/yourcompany)")
    .nullable()
    .notRequired(),
  companyInstagram: Yup.string()
    .transform(v => normalizeUrl(v) || "")
    .url("Please enter a valid URL (e.g. https://www.instagram.com/yourcompany)")
    .nullable()
    .notRequired(),
  companyFacebook: Yup.string()
    .transform(v => normalizeUrl(v) || "")
    .url("Please enter a valid URL (e.g. https://www.facebook.com/yourcompany)")
    .nullable()
    .notRequired(),
  services: Yup.string().max(300),
  aboutCompany: Yup.string().max(300),
});
